import { createApp, type App, type Component } from 'vue';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import 'vuetify/styles';
import '@mdi/font/css/materialdesignicons.css';
import { PiniaColada } from '@pinia/colada';
import { getPinia } from '@/createPiniaSingleton';
import { leanlawDefaults, leanlawLightTheme } from '@/vuetifyTheme';

type VuetifyInstance = ReturnType<typeof createVuetify>;

let vuetifyInstance: VuetifyInstance | undefined;

/**
 * Shared Vuetify instance for standalone Vue apps mounted outside the main
 * Vue-in-Angular bridge (main.js), which also uses it.
 *
 * Safe to install into several apps: Vuetify's `install()` scopes its per-app
 * work in its own `effectScope` and stops only that on unmount, and the theme
 * stylesheet is upserted by a fixed element id — so unmounting one island
 * cannot strip styles from the rest of the app.
 */
export function getVuetify(): VuetifyInstance {
  if (!vuetifyInstance) {
    vuetifyInstance = createVuetify({
      components,
      directives,
      theme: { themes: { light: leanlawLightTheme } },
      defaults: leanlawDefaults,
    });
  }
  return vuetifyInstance;
}

/**
 * Bootstraps a Vue app (shared Pinia + Pinia Colada + Vuetify) for imperative
 * mounting from AngularJS controllers — e.g. a dialog opened on demand, or a
 * nav component mounted into a host element. Callers still call `.mount(host)`.
 *
 * Pinia Colada is installed so islands match `main.js`: the global query and
 * mutation defaults configured there apply to a store's `useQuery`/`useMutation`
 * wherever it is mounted. Note it is not load-bearing for a store merely to
 * *work* — inside a real app `inject()` falls back to Colada's own defaults, so
 * a mutation resolves either way. The "Cannot read properties of undefined
 * (reading 'onSettled')" failure in `.claude/rules/testing.md` is the
 * no-app-context case (a bare `setActivePinia`), which does not apply here.
 *
 * Order matters: `PiniaColada` reads `app.config.globalProperties.$pinia` and
 * throws outside production if Pinia has not been installed first.
 */
export function createVueIsland(
  component: Component,
  rootProps?: Record<string, unknown> | null,
): App {
  return createApp(component, rootProps)
    .use(getPinia())
    .use(PiniaColada)
    .use(getVuetify());
}
